import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CDTAndCustomProcedureSelect, {
  CDTAndCustomProcedureSelectHandle,
  CDTAndCustomProcedureValue,
} from "../../../../components/CDTAndCustomProcedureSelect";
import { EditableProcedureData } from "../../../../types/editableTreatmentPlan";
import { deduplicateCdtCodesStructured } from "../../../../utils/cdtCodeUtils";
import { TreatmentStepEditChips } from "../TreatmentStepChips";
import {
  EditProcedureCdtCell,
  EditAddCodeButtonWrapper,
} from "./EditTreatmentAppointment.styled";

export interface EditProcedureCdtFieldProps {
  appointmentId: number;
  procedureIndex: number;
  procedure: EditableProcedureData;
  onCdtChange: (
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => void;
}

const EditProcedureCdtField: React.FC<EditProcedureCdtFieldProps> = ({
  appointmentId,
  procedureIndex,
  procedure,
  onCdtChange,
}) => {
  const { t } = useTranslation();
  const selectRef = useRef<CDTAndCustomProcedureSelectHandle>(null);
  const [open, setOpen] = useState(false);

  const codeEntries = useMemo(
    () =>
      deduplicateCdtCodesStructured(
        procedure.resolvedCdtCodes.map((code) => ({ code })),
      ),
    [procedure.resolvedCdtCodes],
  );

  const handleOpenDropdown = () => {
    setOpen(true);
  };

  const handleRemoveCode = (codeToRemove: string) => {
    if (procedure.cdtCodes.includes(codeToRemove)) {
      selectRef.current?.removeCode(codeToRemove);
      return;
    }

    if (!procedure.customProcedureId) {
      return;
    }

    const descriptionLines = procedure.explanation.split("\n").filter(Boolean);
    const remainingCodes = procedure.resolvedCdtCodes.filter(
      (code) => code !== codeToRemove,
    );
    const remainingDescriptions = procedure.resolvedCdtCodes
      .map((code, index) => ({ code, description: descriptionLines[index] ?? "" }))
      .filter((item) => item.code !== codeToRemove)
      .map((item) => item.description)
      .filter(Boolean);

    onCdtChange(
      { cdtCodes: remainingCodes, customProcedureId: undefined },
      remainingCodes,
      remainingDescriptions,
      parseFloat(procedure.fee || "0") || 0,
      procedure.durationMinutes,
    );
  };

  const chipValues = codeEntries.map((entry) =>
    entry.count > 1 ? `${entry.code} x${entry.count}` : entry.code,
  );

  return (
    <EditProcedureCdtCell>
      <EditAddCodeButtonWrapper>
        <TreatmentStepEditChips
          values={chipValues}
          onRemove={(displayValue) => {
            const code = displayValue.replace(/ x\d+$/, "");
            handleRemoveCode(code);
          }}
          onAddClick={handleOpenDropdown}
          addButtonVariant="code"
          addButtonLabel={t("editTreatmentPlan.addCode")}
        />
        <CDTAndCustomProcedureSelect
          ref={selectRef}
          hideTrigger
          open={open}
          onOpenChange={setOpen}
          name={`cdt-${appointmentId}-${procedureIndex}`}
          value={{
            cdtCodes: procedure.cdtCodes,
            customProcedureId: procedure.customProcedureId,
          }}
          onChange={onCdtChange}
          placeholder={t("editTreatmentPlan.cdtCodesPlaceholder")}
        />
      </EditAddCodeButtonWrapper>
    </EditProcedureCdtCell>
  );
};

export default EditProcedureCdtField;
